import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Clock,
  Save,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@cloud/shared/ui'
import { orpc } from '@/lib/orpcClient'

// ── Types ────────────────────────────────────────────────────────────────────

type AttendanceStatus = 'present' | 'absent' | 'half_day' | 'wfh' | 'holiday' | 'on_leave'
type Scope = 'all' | 'department' | 'specific'

const STATUS_OPTIONS: { value: AttendanceStatus; label: string; color: string }[] = [
  { value: 'present', label: 'Present', color: 'bg-green-100 text-green-800' },
  { value: 'absent', label: 'Absent', color: 'bg-red-100 text-red-800' },
  { value: 'half_day', label: 'Half Day', color: 'bg-amber-100 text-amber-800' },
  { value: 'wfh', label: 'WFH', color: 'bg-blue-100 text-blue-800' },
  { value: 'holiday', label: 'Holiday', color: 'bg-purple-100 text-purple-800' },
  { value: 'on_leave', label: 'On Leave', color: 'bg-gray-100 text-gray-700' },
]

interface EmployeeRow {
  id: number
  firstName: string
  lastName: string | null
  departmentId: number | null
  status: string | null
}

interface Department {
  id: number
  name: string
}

interface ShiftType {
  id: number
  name: string
  startTime: string
  endTime: string
  color: string | null
}

interface ShiftAssignment {
  employeeId: number
  shiftType: ShiftType | null
}

interface AttendanceRow {
  attendance: {
    id: number
    employeeId: number
    status: string
    checkIn: string | null
    checkOut: string | null
    shiftTypeId: number | null
    overtimeHours: string | null
    notes: string | null
  }
}

interface RowDetails {
  checkIn: string // HH:MM local time
  checkOut: string // HH:MM local time
  overtimeHours: string // numeric string
  notes: string
  shiftTypeId: number | null
}

const EMPTY_DETAILS: RowDetails = {
  checkIn: '',
  checkOut: '',
  overtimeHours: '',
  notes: '',
  shiftTypeId: null,
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Convert an ISO timestamp to HH:MM in the user's local timezone. */
function isoToLocalTime(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return `${hh}:${mm}`
  } catch {
    return ''
  }
}

/** Combine a date string (YYYY-MM-DD) and time string (HH:MM) into ISO format. */
function combineDateTime(date: string, time: string): string | undefined {
  if (!time) return undefined
  return `${date}T${time}:00`
}

// ── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/hr/attendance/mark')({
  component: MarkAttendancePage,
})

function MarkAttendancePage() {
  const today = new Date().toISOString().split('T')[0]
  const [date, setDate] = useState(today)
  const [scope, setScope] = useState<Scope>('all')
  const [departmentId, setDepartmentId] = useState<string>('')
  const [specificIds, setSpecificIds] = useState<number[]>([])
  const [statuses, setStatuses] = useState<Record<number, AttendanceStatus>>({})
  const [details, setDetails] = useState<Record<number, RowDetails>>({})
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  // ── Data ─────────────────────────────────────────────────────────────────

  const { data: employeesData, isLoading: loadingEmployees } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const { data: departmentsData } = useQuery(
    orpc.hr.getDepartments.queryOptions({ input: {} }),
  )

  const { data: shiftAssignmentsData } = useQuery(
    orpc.hr.listShiftAssignments.queryOptions({ input: { activeOnly: true } }),
  )

  const { data: shiftTypesData } = useQuery(
    orpc.hr.listShiftTypes.queryOptions({ input: {} }),
  )

  const { data: existingAttendanceData } = useQuery(
    orpc.hr.listAttendance.queryOptions({
      input: { page: 1, limit: 500, date },
    }),
  )

  const allEmployees = (employeesData?.data ?? []) as EmployeeRow[]
  const activeEmployees = allEmployees.filter((e) => e.status === 'active')
  const departments = (departmentsData ?? []) as Department[]
  const shiftAssignments = (shiftAssignmentsData ?? []) as ShiftAssignment[]
  const shiftTypes = (shiftTypesData ?? []) as ShiftType[]
  const existingRows = (
    (existingAttendanceData as { data?: unknown[] } | null | undefined)?.data ?? []
  ) as AttendanceRow[]

  const deptMap = useMemo(() => new Map(departments.map((d) => [d.id, d.name])), [departments])
  const shiftMap = useMemo(() => {
    const m = new Map<number, ShiftAssignment>()
    for (const sa of shiftAssignments) m.set(sa.employeeId, sa)
    return m
  }, [shiftAssignments])

  // ── Scoped employee list ─────────────────────────────────────────────────

  const scopedEmployees: EmployeeRow[] = useMemo(() => {
    if (scope === 'all') return activeEmployees
    if (scope === 'department') {
      if (!departmentId) return []
      return activeEmployees.filter((e) => e.departmentId === Number(departmentId))
    }
    if (scope === 'specific') {
      const idSet = new Set(specificIds)
      return activeEmployees.filter((e) => idSet.has(e.id))
    }
    return []
  }, [scope, departmentId, specificIds, activeEmployees])

  // Pre-fill statuses + details from existing attendance when the date changes.
  useEffect(() => {
    const nextStatuses: Record<number, AttendanceStatus> = {}
    const nextDetails: Record<number, RowDetails> = {}
    for (const row of existingRows) {
      const a = row.attendance
      nextStatuses[a.employeeId] = a.status as AttendanceStatus
      nextDetails[a.employeeId] = {
        checkIn: isoToLocalTime(a.checkIn),
        checkOut: isoToLocalTime(a.checkOut),
        overtimeHours:
          a.overtimeHours && parseFloat(a.overtimeHours) > 0 ? a.overtimeHours : '',
        notes: a.notes ?? '',
        shiftTypeId: a.shiftTypeId ?? null,
      }
    }
    setStatuses(nextStatuses)
    setDetails(nextDetails)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, existingRows.length])

  // ── Mutation ─────────────────────────────────────────────────────────────

  const bulkMarkMutation = useMutation(
    orpc.hr.bulkMarkAttendance.mutationOptions({
      onSuccess: (data) => {
        const marked = (data as { marked?: number } | null | undefined)?.marked ?? 0
        toast.success(`Attendance marked for ${marked} employee${marked !== 1 ? 's' : ''}`)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  // ── Handlers ─────────────────────────────────────────────────────────────

  const getStatus = (employeeId: number): AttendanceStatus =>
    statuses[employeeId] ?? 'present'

  const getDetails = (employeeId: number): RowDetails => details[employeeId] ?? EMPTY_DETAILS

  const setStatus = (employeeId: number, status: AttendanceStatus) => {
    setStatuses((prev) => ({ ...prev, [employeeId]: status }))
  }

  const patchDetails = (employeeId: number, patch: Partial<RowDetails>) => {
    setDetails((prev) => ({
      ...prev,
      [employeeId]: { ...(prev[employeeId] ?? EMPTY_DETAILS), ...patch },
    }))
  }

  const toggleExpanded = (employeeId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(employeeId)) {
        next.delete(employeeId)
      } else {
        next.add(employeeId)
      }
      return next
    })
  }

  const handleMarkAll = (status: AttendanceStatus) => {
    setStatuses((prev) => {
      const next = { ...prev }
      for (const emp of scopedEmployees) next[emp.id] = status
      return next
    })
  }

  const handleSaveAll = () => {
    if (!date) {
      toast.error('Please select a date')
      return
    }
    if (scopedEmployees.length === 0) {
      toast.error('No employees selected')
      return
    }

    // Validate: check-in must be before check-out per row
    for (const emp of scopedEmployees) {
      const d = getDetails(emp.id)
      if (d.checkIn && d.checkOut && d.checkOut <= d.checkIn) {
        toast.error(`Check-out must be after check-in for ${emp.firstName} ${emp.lastName ?? ''}`)
        return
      }
    }

    const entries = scopedEmployees.map((emp) => {
      const d = getDetails(emp.id)
      return {
        employeeId: emp.id,
        status: getStatus(emp.id),
        checkIn: combineDateTime(date, d.checkIn),
        checkOut: combineDateTime(date, d.checkOut),
        shiftTypeId: d.shiftTypeId ?? undefined,
        overtimeHours: d.overtimeHours || undefined,
        notes: d.notes || undefined,
      }
    })
    bulkMarkMutation.mutate({ date, entries })
  }

  const existingCount = scopedEmployees.filter((emp) =>
    existingRows.some((r) => r.attendance.employeeId === emp.id),
  ).length

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="container mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/hr/attendance">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Mark Attendance</h1>
            <p className="text-muted-foreground">
              Expand a row to set check-in/out, overtime, and notes
            </p>
          </div>
        </div>
        <Button
          onClick={handleSaveAll}
          disabled={bulkMarkMutation.isPending || loadingEmployees || scopedEmployees.length === 0}
        >
          <Save className="h-4 w-4 mr-2" />
          {bulkMarkMutation.isPending ? 'Saving...' : `Save (${scopedEmployees.length})`}
        </Button>
      </div>

      {/* Scope + Date */}
      <Card className="mb-4">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="flex gap-3 items-center">
              <label className="text-sm font-medium whitespace-nowrap">Date</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-[180px]"
              />
            </div>

            <div className="flex gap-3 items-center">
              <label className="text-sm font-medium whitespace-nowrap">Who</label>
              <Tabs value={scope} onValueChange={(v) => setScope(v as Scope)}>
                <TabsList>
                  <TabsTrigger value="all">All employees</TabsTrigger>
                  <TabsTrigger value="department">By department</TabsTrigger>
                  <TabsTrigger value="specific">Specific</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {scope === 'department' && (
            <div className="flex gap-3 items-center">
              <label className="text-sm font-medium whitespace-nowrap">Department</label>
              <Select value={departmentId} onValueChange={setDepartmentId}>
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Select a department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => {
                    const count = activeEmployees.filter(
                      (e) => e.departmentId === dept.id,
                    ).length
                    return (
                      <SelectItem key={dept.id} value={dept.id.toString()}>
                        {dept.name} ({count})
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {scope === 'specific' && (
            <EmployeeMultiSelect
              employees={activeEmployees}
              deptMap={deptMap}
              selectedIds={specificIds}
              onChange={setSpecificIds}
            />
          )}
        </CardContent>
      </Card>

      {/* Quick actions */}
      {scopedEmployees.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-1">Mark all as:</span>
          {STATUS_OPTIONS.slice(0, 4).map((opt) => (
            <Button
              key={opt.value}
              variant="outline"
              size="sm"
              onClick={() => handleMarkAll(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
          {expanded.size > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => setExpanded(new Set())}
            >
              Collapse all
            </Button>
          )}
        </div>
      )}

      {/* Info banner */}
      {existingCount > 0 && (
        <Card className="mb-4 border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <p className="text-sm text-blue-800">
              {existingCount} of the selected employee{existingCount !== 1 ? 's' : ''} already
              have attendance recorded for this date. Current values are pre-filled — saving
              will overwrite.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Employee Table */}
      <Card>
        <CardContent className="p-0">
          {loadingEmployees ? (
            <div className="space-y-2 p-6">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : scopedEmployees.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-muted-foreground">
                {scope === 'department' && !departmentId
                  ? 'Select a department to see its employees'
                  : scope === 'specific' && specificIds.length === 0
                    ? 'Add employees to the list using the selector above'
                    : 'No matching employees'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]" />
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead className="w-[200px]">Status</TableHead>
                  {scope === 'specific' && <TableHead className="w-[60px]" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {scopedEmployees.map((emp) => {
                  const shift = shiftMap.get(emp.id)
                  const hasExisting = existingRows.some(
                    (r) => r.attendance.employeeId === emp.id,
                  )
                  const isOpen = expanded.has(emp.id)
                  const rowDetails = getDetails(emp.id)
                  const hasDetailValues = Boolean(
                    rowDetails.checkIn ||
                      rowDetails.checkOut ||
                      rowDetails.overtimeHours ||
                      rowDetails.notes ||
                      rowDetails.shiftTypeId,
                  )
                  const colSpan = scope === 'specific' ? 6 : 5
                  return (
                    <>
                      <TableRow
                        key={`${emp.id}-main`}
                        className={cn(
                          hasExisting && 'bg-muted/30',
                          isOpen && 'border-b-0',
                        )}
                      >
                        <TableCell className="w-[40px]">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => toggleExpanded(emp.id)}
                            title={isOpen ? 'Collapse' : 'Show details'}
                          >
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {emp.firstName} {emp.lastName ?? ''}
                            {hasDetailValues && !isOpen && (
                              <Badge
                                variant="outline"
                                className="bg-slate-100 text-slate-700 font-normal text-[10px] px-1.5"
                              >
                                details
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {emp.departmentId ? (deptMap.get(emp.departmentId) ?? '-') : '-'}
                        </TableCell>
                        <TableCell>
                          {shift?.shiftType ? (
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-sm">
                                {shift.shiftType.name}{' '}
                                <span className="text-muted-foreground">
                                  ({shift.shiftType.startTime}–{shift.shiftType.endTime})
                                </span>
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={getStatus(emp.id)}
                            onValueChange={(v) => setStatus(emp.id, v as AttendanceStatus)}
                          >
                            <SelectTrigger className="w-[160px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        {scope === 'specific' && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                setSpecificIds((ids) => ids.filter((id) => id !== emp.id))
                              }
                              title="Remove from list"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                      {isOpen && (
                        <TableRow key={`${emp.id}-detail`} className="bg-muted/20 hover:bg-muted/20">
                          <TableCell colSpan={colSpan} className="py-4">
                            <DetailEditor
                              date={date}
                              details={rowDetails}
                              shiftTypes={shiftTypes}
                              defaultShiftTypeId={shift?.shiftType?.id ?? null}
                              onChange={(patch) => patchDetails(emp.id, patch)}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Summary footer */}
      {scopedEmployees.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((opt) => {
              const count = scopedEmployees.filter((emp) => getStatus(emp.id) === opt.value).length
              if (count === 0) return null
              return (
                <Badge key={opt.value} variant="outline" className={opt.color}>
                  {opt.label}: {count}
                </Badge>
              )
            })}
          </div>
          <Button
            onClick={handleSaveAll}
            disabled={bulkMarkMutation.isPending || loadingEmployees}
          >
            <Save className="h-4 w-4 mr-2" />
            {bulkMarkMutation.isPending
              ? 'Saving...'
              : `Save (${scopedEmployees.length})`}
          </Button>
        </div>
      )}
    </div>
  )
}

// ── Per-row detail editor ────────────────────────────────────────────────────

interface DetailEditorProps {
  date: string
  details: RowDetails
  shiftTypes: ShiftType[]
  defaultShiftTypeId: number | null
  onChange: (patch: Partial<RowDetails>) => void
}

function DetailEditor({
  date,
  details,
  shiftTypes,
  defaultShiftTypeId,
  onChange,
}: DetailEditorProps) {
  const prefillFromShift = (shiftId: number) => {
    const st = shiftTypes.find((s) => s.id === shiftId)
    if (!st) return
    onChange({
      shiftTypeId: shiftId,
      checkIn: st.startTime,
      checkOut: st.endTime,
    })
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 px-2">
      {/* Check-in */}
      <div className="md:col-span-2">
        <label className="text-xs font-medium text-muted-foreground mb-1 block">
          Check-in
        </label>
        <Input
          type="time"
          value={details.checkIn}
          onChange={(e) => onChange({ checkIn: e.target.value })}
        />
      </div>

      {/* Check-out */}
      <div className="md:col-span-2">
        <label className="text-xs font-medium text-muted-foreground mb-1 block">
          Check-out
        </label>
        <Input
          type="time"
          value={details.checkOut}
          onChange={(e) => onChange({ checkOut: e.target.value })}
        />
      </div>

      {/* Overtime */}
      <div className="md:col-span-2">
        <label className="text-xs font-medium text-muted-foreground mb-1 block">
          Overtime (hrs)
        </label>
        <Input
          type="number"
          min={0}
          step={0.25}
          placeholder="0"
          value={details.overtimeHours}
          onChange={(e) => onChange({ overtimeHours: e.target.value })}
        />
      </div>

      {/* Shift override */}
      <div className="md:col-span-3">
        <label className="text-xs font-medium text-muted-foreground mb-1 block">
          Shift
        </label>
        <Select
          value={details.shiftTypeId?.toString() ?? defaultShiftTypeId?.toString() ?? 'none'}
          onValueChange={(v) => {
            if (v === 'none') {
              onChange({ shiftTypeId: null })
            } else {
              prefillFromShift(Number(v))
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="No shift" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No shift</SelectItem>
            {shiftTypes.map((st) => (
              <SelectItem key={st.id} value={st.id.toString()}>
                {st.name} ({st.startTime}–{st.endTime})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date reference (read-only info) */}
      <div className="md:col-span-3 flex items-end">
        <p className="text-xs text-muted-foreground">
          Times are for <span className="font-medium">{date}</span> in your local timezone.
        </p>
      </div>

      {/* Notes */}
      <div className="md:col-span-12">
        <label className="text-xs font-medium text-muted-foreground mb-1 block">
          Notes
        </label>
        <Textarea
          placeholder="Optional notes..."
          value={details.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          className="min-h-[60px]"
        />
      </div>
    </div>
  )
}

// ── Multi-select employee picker ─────────────────────────────────────────────

interface EmployeeMultiSelectProps {
  employees: EmployeeRow[]
  deptMap: Map<number, string>
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

function EmployeeMultiSelect({
  employees,
  deptMap,
  selectedIds,
  onChange,
}: EmployeeMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const selectedSet = new Set(selectedIds)

  const toggle = (id: number) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  const clearAll = () => onChange([])
  const selectAll = () => onChange(employees.map((e) => e.id))

  return (
    <div className="flex items-start gap-3 flex-wrap">
      <label className="text-sm font-medium pt-2 whitespace-nowrap">Employees</label>
      <div className="flex-1 min-w-[260px] space-y-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
            >
              {selectedIds.length === 0
                ? 'Add employees...'
                : `${selectedIds.length} employee${selectedIds.length !== 1 ? 's' : ''} selected`}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[360px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search employees..." />
              <CommandList>
                <CommandEmpty>No employees found</CommandEmpty>
                <CommandGroup>
                  <div className="flex items-center justify-between px-2 py-1.5 text-xs text-muted-foreground">
                    <button
                      type="button"
                      className="hover:text-foreground"
                      onClick={selectAll}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="hover:text-foreground"
                      onClick={clearAll}
                    >
                      Clear
                    </button>
                  </div>
                  {employees.map((emp) => {
                    const deptName = emp.departmentId
                      ? (deptMap.get(emp.departmentId) ?? '')
                      : ''
                    const label = `${emp.firstName} ${emp.lastName ?? ''}`
                    const checked = selectedSet.has(emp.id)
                    return (
                      <CommandItem
                        key={emp.id}
                        value={`${label} ${deptName}`}
                        onSelect={() => toggle(emp.id)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            checked ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="flex-1">{label}</span>
                        {deptName && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {deptName}
                          </span>
                        )}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
